import React, { useState, useCallback } from "react";
import { Box, Paper, Typography, ToggleButtonGroup, ToggleButton } from "@mui/material";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { useMsal } from "@azure/msal-react";

import { useAuth } from "../AuthProvider";
import { usePermissions } from "../rbac/PermissionsProvider";
import { PERMISSIONS } from "../rbac/permissions";
import { catalogTokens, parseApiError } from "./VendorCatalogTheme";
import VendorCatalogSearch from "./VendorCatalogSearch";
import { getApiToken } from "../authUtils";
import urlconfig from "../config/urlconfig";
import VendorCatalogUpload from "./VendorCatalogUpload";
import VendorCatalogCollectionManager from "./VendorCatalogCollectionManager";
import KPICards from "../shared/kpi/KPICards";
import { OVERALL_DEFS, mapOverallStats } from "./VendorCatalogKPI/VendorCatalogKpiDefs";

const VendorCatalogFinder = () => {
  const { account } = useAuth();
  const { instance } = useMsal();
  const { can } = usePermissions();
  const canUpload = can(PERMISSIONS.VENDOR_CATALOG_MANAGE);
  const [tab, setTab] = useState("find");

  // Auth-aware fetcher for our own backend (used by the "overall" KPI row).
  const authedFetch = useCallback(
    async (url) => {
      const token = await getApiToken(instance, account);
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(await parseApiError(res));
      const text = await res.text();
      return text ? JSON.parse(text) : {};
    },
    [instance, account]
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: `${catalogTokens.radius.lg}px`,
            bgcolor: catalogTokens.color.primarySoft,
            color: catalogTokens.color.primary,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <MenuBookIcon />
        </Box>
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ color: catalogTokens.color.textPrimary, lineHeight: 1.2 }}>
            Vendor Catalogs
          </Typography>
          <Typography variant="body2" sx={{ color: catalogTokens.color.textSecondary }}>
            Find the latest vendor catalogs by category and price range to pitch to clients.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <KPICards
          cards={OVERALL_DEFS}
          columns={{ xs: 6, sm: 4, md: 2.4 }}
          fetchConfig={{
            fetcher: authedFetch,
            url: `${urlconfig.backend_api_base_url}/api/v2/vendor-catalogs/stats`,
            mapResponse: mapOverallStats,
          }}
        />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <ToggleButtonGroup
          value={tab}
          exclusive
          onChange={(_, value) => value && setTab(value)}
          sx={{
            bgcolor: catalogTokens.color.surfaceMuted,
            borderRadius: `${catalogTokens.radius.xl}px`,
            p: 0.5,
            "& .MuiToggleButtonGroup-grouped": {
              border: 0,
              borderRadius: `${catalogTokens.radius.xl}px !important`,
              textTransform: "none",
              fontWeight: 700,
              px: 2.5,
              color: catalogTokens.color.textSecondary,
              "&.Mui-selected": {
                bgcolor: catalogTokens.color.surface,
                color: catalogTokens.color.primary,
                boxShadow: catalogTokens.shadow.sm,
              },
              "&.Mui-selected:hover": { bgcolor: catalogTokens.color.surface },
            },
          }}
        >
          <ToggleButton value="find">
            <SearchIcon fontSize="small" sx={{ mr: 1 }} />
            Find Catalogs
          </ToggleButton>
          {canUpload && (
            <ToggleButton value="upload">
              <UploadFileIcon fontSize="small" sx={{ mr: 1 }} />
              Upload Catalog
            </ToggleButton>
          )}
          {canUpload && (
            <ToggleButton value="collections">
              <LocalOfferOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
              Manage Collections
            </ToggleButton>
          )}
        </ToggleButtonGroup>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: `${catalogTokens.radius.xl}px`,
          border: `1px solid ${catalogTokens.color.border}`,
          p: { xs: 2, md: 3 },
        }}
      >
        {tab === "find" && <VendorCatalogSearch />}
        {tab === "upload" && canUpload && <VendorCatalogUpload />}
        {tab === "collections" && canUpload && <VendorCatalogCollectionManager />}
      </Paper>
    </Box>
  );
};

export default VendorCatalogFinder;
